/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import Button from "@react/react-spectrum/Button";
import classNames from "classnames";
import Tooltip from "@react/react-spectrum/Tooltip";
import OverlayTrigger from "@react/react-spectrum/OverlayTrigger";
import { FormattedMessage } from "react-intl";
import {
  isCurrentDeviceMobile,
  isCurrentDeviceTablet
} from "dc-preview-utils/src/Utils/DeviceUtils";
import withMediaQuery from "dc-preview-utils/src/components/withMediaQuery";
import CustomIcon from "./../CustomIcon";
import ButtonWrapperCss from "./ButtonWrapper.css";
import { mqTypes } from "../../Constants/enums";
import {
  FC_ACTIVE,
  HC_BUTTON_DEF_STYLE,
  HC_BUTTON_SELECTED_STYLE
} from "../../Constants/constants";
import VerbBadge from "../Badge/Badge";

const withShortcutLabel = (txt, shortcutLabel) =>
  shortcutLabel ? `${txt} (${shortcutLabel})` : txt;

const FormattedMsgWithShortcut = ({ shortcutLabel, ...props }) => (
  <FormattedMessage {...props}>
    {txt => withShortcutLabel(txt, shortcutLabel)}
  </FormattedMessage>
);

FormattedMsgWithShortcut.propTypes = {
  shortcutLabel: PropTypes.string
};

FormattedMsgWithShortcut.defaultProps = {
  shortcutLabel: null
};

const svgElementTypes = [
  "audio",
  "canvas",
  "circle",
  "ellipse",
  "foreignObject",
  "iframe",
  "image",
  "line",
  "path",
  "polygon",
  "polyline",
  "rect",
  "text",
  "textPath",
  "tspan",
  "video"
];

class ButtonWrapper extends Component {
  constructor(props) {
    super(props);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.isHoverActive = false;
    this.buttonColor = "";
    this.changeIconColor = false;
  }

  componentDidMount() {
    this.overrideDefaultStyle(this.overridingStyle);
    if (this.props.shouldActivate)
      window.addEventListener("mouseup", this.onMouseUp);
  }

  componentDidUpdate() {
    // Maintain default behavior for High Contrast Cases
    this.changeIconColor = this.props.shouldOverrideIcon && !this.isHCEnabled;
    if (this.props.shouldDisable) this.isHoverActive = false;
    this.overrideDefaultStyle(this.overridingStyle);
  }

  componentWillUnmount() {
    if (this.props.shouldActivate)
      window.removeEventListener("mouseup", this.onMouseUp);
  }

  onMouseEnter() {
    this.isHoverActive = true;
    this.overrideDefaultStyle(this.overridingStyle);
  }

  onMouseLeave() {
    this.isHoverActive = false;
    this.overrideDefaultStyle(this.overridingStyle);
  }

  onMouseUp = () => {
    this.isActive = false;
    this.overrideDefaultStyle(this.overridingStyle);
  };

  onMouseDown = () => {
    this.isActive = true;
    this.overrideDefaultStyle(this.overridingStyle);
  };

  getHCStyle() {
    if (this.props.isSelected) return HC_BUTTON_SELECTED_STYLE;
    return HC_BUTTON_DEF_STYLE;
  }

  get isHCEnabled() {
    return this.props.mqState === FC_ACTIVE;
  }

  get overridingStyle() {
    if (this.isHCEnabled) return this.getHCStyle();
    if (this.props.isSelected) return this.props.selectedStyle;
    if (this.isActive) return this.props.activeStyle;
    if (this.isHoverActive) return this.props.hoverStyle;
    return this.props.defaultStyle;
  }

  overrideDefaultStyle(overridingStyle) {
    if (this.props.icon) {
      this.setIconStyle(overridingStyle);
      this.setButtonStyle(overridingStyle);
    }
    this.setContainerStyle(this.props.containerStyle);
  }

  setContainerStyle(containerStyle) {
    if (this.props.shouldDisable) {
      if (this.isHCEnabled) {
        this.containerRef.style.opacity = 1;
      } else {
        const { opacity } = containerStyle;
        if (opacity) {
          this.containerRef.style.opacity = opacity;
        } else {
          this.containerRef.style.opacity = "";
        }
      }
    }
  }

  unsetColorChangeOnSVG(ref) {
    const svg = ref.getElementsByTagName("svg");
    if (svg.length) {
      svg[0].style.fill = "";
      svgElementTypes.forEach(elementType => {
        const elements = ref.getElementsByTagName(elementType);
        Array.from(elements).forEach(elem => {
          const element = elem;
          element.style.fill = "";
        });
      });
    }
  }

  setIconStyle(overridingStyle) {
    const { color, useCustomFontColor, isMultiColorIcon } = overridingStyle;

    // Set svg color to currentColor when overriding svg and high contrast is enabled
    if (color || (this.props.shouldOverrideIcon && this.isHCEnabled)) {
      let svgColor = this.buttonColor;
      const svgIconColor = useCustomFontColor ? color : "currentColor";

      const svg = this.containerRef.getElementsByTagName("svg");
      if (svg.length) {
        const fillColor = svg[0].getAttribute("fill");
        if (fillColor) {
          svgColor = fillColor;
        }
        svg[0].style.fill = this.props.shouldOverrideIcon
          ? svgIconColor
          : color;
      }

      if (isMultiColorIcon) {
        // fill color is set on the first path of svg, assuming that the first path is the main path
        const element = this.containerRef.querySelector("svg > path");
        if (element) {
          const fillColor = element.getAttribute("fill");
          if (fillColor) {
            svgColor = fillColor;
          }
          element.style.fill = this.props.shouldOverrideIcon
            ? svgIconColor
            : color;
        }
      } else {
        svgElementTypes.forEach(elementType => {
          const elements = this.containerRef.getElementsByTagName(elementType);
          Array.from(elements).forEach(elem => {
            const element = elem;
            const fillColor = elem.getAttribute("fill");
            if (fillColor) {
              svgColor = fillColor;
            }
            element.style.fill = this.props.shouldOverrideIcon
              ? svgIconColor
              : color;
          });
        });
      }

      this.buttonColor = svgColor;
    } else {
      this.unsetColorChangeOnSVG(this.containerRef);
    }
  }

  setButtonStyle(overridingStyle) {
    const { background, padding, color, borderRadius } = overridingStyle;
    const { buttonRef } = this.button;
    if (background) {
      buttonRef.style.background = background;
      if (this.changeIconColor) {
        buttonRef.style.color = color || this.buttonColor;
      }
      if (padding) buttonRef.style.padding = padding;
    }
    if (borderRadius) {
      buttonRef.style.borderRadius = borderRadius;
    }
    if (this.props.cursorType) {
      buttonRef.style.cursor = this.props.cursorType;
    }
  }

  getButton(intlAriaLabel) {
    // Override shouldDisable as we have css disabled buttons for enabling click events and showing show toast.
    const ariaDisabled =
      typeof this.props.ariaDisabled === "undefined"
        ? this.props.shouldDisable
        : this.props.ariaDisabled;
    return (
      <Button
        id={this.props.id}
        ref={button => {
          this.button = button;
          if (this.props.refCallback) this.props.refCallback(button);
        }}
        disabled={this.props.shouldDisable}
        aria-disabled={ariaDisabled}
        onMouseEnter={this.props.shouldEnableHover ? this.onMouseEnter : null}
        onMouseLeave={this.props.shouldEnableHover ? this.onMouseLeave : null}
        aria-label={intlAriaLabel}
        tabIndex={this.props.tabIndex}
        onClick={this.props.clickHandler}
        onMouseDown={this.props.shouldActivate ? this.onMouseDown : null}
        quiet={this.props.quiet}
        label={this.props.label}
        variant={this.props.variant}
        icon={
          this.props.icon ? (
            <CustomIcon
              url={this.props.icon}
              svgClassName={this.props.svgClassName}
            />
          ) : null
        }
        holdAffordance={this.props.holdAffordance}
        className={this.props.buttonClass}
      >
        {this.props.children}
        {this.props.badgeList.length > 0 &&
          this.props.badgeList.map(badge => (
            <VerbBadge variant={badge.variant}>{badge.badgeString}</VerbBadge>
          ))}
      </Button>
    );
  }

  /**
   * Instead of rendering a simple button, we render a button with
   * hidden tool-tip. This is a hack to ensure that focus is not lost when
   * tool-tip is enabled/disabled dynamically
   *
   */
  getTooltipButton() {
    const hideTooltip =
      isCurrentDeviceMobile() ||
      isCurrentDeviceTablet() ||
      !this.props.tooltipString ||
      this.props.shouldDisableTooltip;
    return intlAriaLabel => (
      <FormattedMessage id={this.props.tooltipString || "default"}>
        {tooltipString => (
          <OverlayTrigger
            trigger="hover"
            placement={this.props.tooltipPlacement}
            delayShow={this.props.tooltipDelay}
            show={this.props.isControlled}
          >
            <div>
              {this.getButton(
                withShortcutLabel(intlAriaLabel, this.props.shortcutLabel)
              )}
            </div>
            <Tooltip
              variant={this.props.tooltipVariant}
              className={
                hideTooltip
                  ? ButtonWrapperCss.InvisibleTooltip
                  : this.props.tooltipClass
              }
            >
              {withShortcutLabel(tooltipString, this.props.shortcutLabel)}
            </Tooltip>
          </OverlayTrigger>
        )}
      </FormattedMessage>
    );
  }

  render() {
    const iconClass = classNames(
      ButtonWrapperCss.Button,
      {
        [ButtonWrapperCss.ButtonDisabled]: this.props.shouldDisable
      },
      {
        [ButtonWrapperCss.ButtonHoverEnabled]:
          this.props.shouldEnableHover &&
          (this.isHCEnabled || !this.props.shouldOverrideIcon)
      },
      {
        [ButtonWrapperCss.ButtonSelected]:
          this.props.isSelected &&
          (this.isHCEnabled || !this.props.shouldOverrideIcon)
      },
      this.props.className
    );
    const button = this.getTooltipButton();

    return (
      <Fragment>
        <div
          className={iconClass}
          ref={containerRef => {
            this.containerRef = containerRef;
          }}
          style={this.props.containerStyle}
          data-testid={
            this.props["data-testid"]
              ? this.props["data-testid"]
              : this.props.ariaLabel
          }
          role={this.props.role}
        >
          <FormattedMessage id={this.props.ariaLabel}>
            {button}
          </FormattedMessage>
        </div>
        <div />
      </Fragment>
    );
  }
}

ButtonWrapper.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.element,
  shouldDisableTooltip: PropTypes.bool,
  tooltipString: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  tooltipDelay: PropTypes.number,
  ariaLabel: PropTypes.string,
  ariaDisabled: PropTypes.bool,
  tabIndex: PropTypes.number,
  clickHandler: PropTypes.func,
  className: PropTypes.string,
  svgClassName: PropTypes.string,
  defaultStyle: PropTypes.objectOf(PropTypes.string),
  shouldEnableHover: PropTypes.bool,
  hoverStyle: PropTypes.objectOf(PropTypes.string),
  shouldActivate: PropTypes.bool,
  activeStyle: PropTypes.objectOf(PropTypes.string),
  isSelected: PropTypes.bool,
  selectedStyle: PropTypes.objectOf(PropTypes.string),
  shouldDisable: PropTypes.bool,
  containerStyle: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool])
  ),
  quiet: PropTypes.bool,
  variant: PropTypes.string,
  holdAffordance: PropTypes.bool,
  mqState: PropTypes.string.isRequired,
  tooltipClass: PropTypes.string,
  cursorType: PropTypes.string,
  buttonClass: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  refCallback: PropTypes.func,
  role: PropTypes.string,
  shortcutLabel: PropTypes.string,
  shouldOverrideIcon: PropTypes.bool,
  "data-testid": PropTypes.string,
  badgeList: PropTypes.arrayOf(
    PropTypes.objectOf(PropTypes.string, PropTypes.string)
  ),
  tooltipVariant: PropTypes.string,
  isControlled: PropTypes.bool
};

ButtonWrapper.defaultProps = {
  id: null,
  label: null,
  icon: null,
  shouldDisableTooltip: false,
  tooltipString: "",
  tooltipPlacement: "top",
  tooltipDelay: 1000,
  ariaLabel: "default",
  ariaDisabled: undefined,
  tabIndex: 0,
  clickHandler: () => {},
  className: "",
  defaultStyle: {
    color: null,
    background: null
  },
  shouldEnableHover: false,
  hoverStyle: {
    color: null,
    background: null
  },
  shouldActivate: false,
  activeStyle: {
    color: null,
    background: null
  },
  isSelected: false,
  selectedStyle: {
    color: null,
    background: null
  },
  shouldDisable: false,
  containerStyle: {
    opacity: ""
  },
  quiet: true,
  variant: "action",
  holdAffordance: false,
  tooltipClass: null,
  cursorType: null,
  buttonClass: null,
  svgClassName: null,
  children: null,
  refCallback: null,
  role: null,
  shortcutLabel: null,
  shouldOverrideIcon: false,
  badgeList: [],
  "data-testid": null,
  tooltipVariant: "",
  isControlled: undefined
};

export default withMediaQuery(ButtonWrapper, mqTypes.FORCED_COLORS);
